import { type PropsWithChildren } from 'react';
import { type SerializeFrom } from '@remix-run/node';

import { type Repub, REPUBS } from '@cx-modules/constants';

import { getFooterNav } from '~/api/footer-nav-api/footer-nav-api.service';
import { getNavItemForHeader } from '~/api/nav-api/nav-api.service';
import { getSiteConfig } from '~/api/site-config-api/site-config.service';
import { type SiteConfig } from '~/api/site-config-api/types';
import Betslip from '~/components/betslip/betslip';
import FooterNav from '~/components/FooterNav';
import FooterOC from '~/components/FooterOC';
import FooterUS from '~/components/FooterUS';
import Header from '~/components/Header';
import { PremiumModal } from '~/components/premium-modal';
import { useAction } from '~/hooks/use-action';
import { FeatureFlagProvider } from '~/providers/feature-flag-provider';
import { LoaderProvider } from '~/providers/loader-context';
import ModalProvider from '~/providers/modal-context';
import { MyOCProvider } from '~/providers/myoc-context';
import { ServicesProvider } from '~/providers/services-context';
import { MyOC } from '~/scripts/myoddschecker/MyOC';
import { type Loggable } from '~/traits/Loggable';
import { getPromiseResult } from '~/utils/getPromiseResult';

import { type loader } from '../root-loader';

export type StandardLayoutLoaderData = {
	footerData: any;
	navData: any;
	siteConfig: SiteConfig;
};

export const standardLayoutLoader = async ({
	context,
	repub,
	url,
}: {
	context: Loggable;
	repub: Repub;
	url: URL;
}): Promise<StandardLayoutLoaderData> => {
	const [navDataResult, footerDataResult, siteConfigResult] =
		await Promise.allSettled([
			getNavItemForHeader.call(context, repub, url.pathname),
			getFooterNav.call(context, repub, url.pathname),
			getSiteConfig.call(context, repub),
		]);

	const navData = getPromiseResult(navDataResult);
	const footerData = getPromiseResult(footerDataResult);
	const siteConfig = getPromiseResult(siteConfigResult);

	if (!navData || !footerData || !siteConfig) {
		throw new Response(null, { status: 500 });
	}

	return {
		footerData,
		navData,
		siteConfig,
	};
};

export const StandardLayout = ({
	DISABLE_ODDS_FORMAT_SELECTION,
	baseUrl,
	baseUrlWithProtocolAndRepub,
	children,
	countryCode,
	currentPath,
	deviceType,
	featureToggleDefinitions,
	footerData,
	isRebrandEnabled,
	labels,
	myocConfig,
	myocData,
	navData,
	repub,
	resourcesUrl,
	showMyOC,
	siteConfig,
	state,
}: PropsWithChildren<SerializeFrom<typeof loader>>) => {
	const { submitAction } = useAction();

	const renderBetslip = () => {
		if (currentPath !== 'betslip') {
			return <Betslip betslipOpen={false} />;
		}

		return null;
	};

	const renderMyOC = () => {
		if (!showMyOC) return null;

		return (
			<>
				<MyOC />
				<PremiumModal />
			</>
		);
	};

	return (
		<FeatureFlagProvider featureToggleDefinitions={featureToggleDefinitions}>
			<LoaderProvider
				DISABLE_ODDS_FORMAT_SELECTION={DISABLE_ODDS_FORMAT_SELECTION}
				apiResponse={undefined}
				baseUrl={baseUrl}
				baseUrlWithProtocolAndRepub={baseUrlWithProtocolAndRepub}
				currentPath={currentPath}
				deviceType={deviceType}
				state={state}
			>
				<MyOCProvider
					myocConfig={myocConfig}
					myocData={myocData}
					submit={submitAction}
				>
					<ModalProvider showMyOC={myocConfig.showMyOC}>
						<ServicesProvider
							NODE_ENV={ENV.NODE_ENV}
							countryCode={countryCode}
							labels={labels}
							repub={repub}
							siteConfig={siteConfig}
							state={state}
						>
							<Header
								deviceType={deviceType}
								isRebrandEnabled={isRebrandEnabled}
								navData={navData}
								repub={repub}
								resourcesUrl={resourcesUrl}
							/>
							{children}
							<footer id="mainAppFooter">
								<FooterNav
									categoryLinks={footerData.categoryLinks}
									deviceType={deviceType}
									repub={repub}
								/>
								{repub === REPUBS.OC.name ? (
									<FooterOC />
								) : (
									<FooterUS
										generalLinks={footerData.generalLinks}
										isRebrandEnabled={isRebrandEnabled}
										resourcesUrl={resourcesUrl}
										socialLinks={footerData.socialLinks}
									/>
								)}
							</footer>
							{renderBetslip()}
							{renderMyOC()}
						</ServicesProvider>
					</ModalProvider>
				</MyOCProvider>
			</LoaderProvider>
		</FeatureFlagProvider>
	);
};
